//@ts-strict-ignore
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import TraceabilityReportQuery from '../../graphql/queries/product/TraceabilityReportQuery.graphql';
import {
  ILotAttributeValue,
  ISalesOrderLine,
  ITraceabilityReportQueryResult,
  ITraceabilityReportQueryVariables,
  ITraceable,
  ITraceableEdge,
  Maybe,
} from '../../graphql/schema/p4types';
import { default as Form } from '../../components/connectedForm';
import EditableDataGrid from '../../components/EditableDataGrid';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';
import getProductDP from '../../utils/getProductDP';
import Formatters from '../../components/grid-addons/formatters';
import { ProgressIndicator } from '@fluentui/react';

const RESULT_COLUMN_META = [
  {
    name: 'Customer',
    key: 'salesOrderLine.salesOrder.customer.name',
  },
  {
    name: 'Order',
    key: 'salesOrderLine.salesOrder.orderNumber',
    editable: false,
  },
  {
    name: 'Product',
    key: 'stockLot.stock.product.reference',
  },
  {
    name: 'Quantity',
    key: 'quantity',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: (rowData) => getProductDP(rowData, 'stockLot.stock.product') },
    },
  },
];

type ITraceableWithLotDetail = ITraceable & { [key: `lot-${string}`]: Maybe<string> };

const TraceabilityDetails = ({
  salesOrderLine,
  contentHeight,
}: {
  salesOrderLine: ISalesOrderLine;
  contentHeight: number;
}) => {
  const [results, setResults] = useState<Maybe<Array<ITraceableWithLotDetail>>>();
  const [resultLotAttributes, setResultLotAttributes] = useState<Array<ILotAttributeValue>>([]);

  const { loading } = useQuery<ITraceabilityReportQueryResult, ITraceabilityReportQueryVariables>(
    TraceabilityReportQuery,
    {
      variables: {
        traceabilityCriteria: {
          salesOrderLineId: salesOrderLine.id,
        },
      },
      onCompleted(data) {
        const lotAttributes: Array<ILotAttributeValue> = [];

        const results = data.data?.edges.map((result: ITraceableEdge) => {
          const stockLotAttrs: Array<ILotAttributeValue> = result.node?.stockLot
            ?.lotAttributeValues as Array<ILotAttributeValue>;

          const withLotDetail: ITraceableWithLotDetail = { ...result.node };

          stockLotAttrs.map((stockLotAttr) => {
            if (!stockLotAttr.attributeConfig?.internal) {
              if (!lotAttributes[stockLotAttr.attributeConfig?.id]) {
                lotAttributes[stockLotAttr.attributeConfig?.id] = stockLotAttr;
              }
              withLotDetail[`lot-${stockLotAttr.attributeConfig?.id}`] = stockLotAttr.value;
            }
          });

          return withLotDetail;
        });

        setResultLotAttributes(Object.values(lotAttributes));
        setResults(results);
      },
      fetchPolicy: 'no-cache',
    }
  );

  if (!salesOrderLine?.id) {
    return <p>No line selected</p>;
  } else if (!results) {
    return loading ? (
      <ProgressIndicator label="Loading" description="Fetching traceability detail" />
    ) : (
      <p>No details found</p>
    );
  }

  const columnMeta: Array<IEditableDataGridColumnDef> = [...RESULT_COLUMN_META];
  resultLotAttributes.forEach((lotAttribute) => {
    columnMeta.push({
      name: lotAttribute.attributeConfig?.title,
      key: `lot-${lotAttribute.attributeConfig?.id}`,
    });
  });

  return (
    <Form.Fieldset cols={1}>
      <EditableDataGrid
        columns={columnMeta}
        listData={results}
        rowsCount={results.length}
        contentHeight={contentHeight}
      />
    </Form.Fieldset>
  );
};

export default TraceabilityDetails;
